import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, forwardRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SearchableSelectService } from './searchable-select.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true
    }
  ]
})
export class SearchableSelectComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  @Input() options: string[] = [];
  @Input() placeholder: string = 'Search or select...';
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() labelClass: string = 'form-label fw-bold';
  @Input() inputClass: string = 'form-control rounded-4 bg-light p-2';
  @Input() noResultsText: string = 'No results found';
  @Input() disabled: boolean = false;
  
  @Output() selectionChange = new EventEmitter<string>();

  searchTerm: string = '';
  filteredOptions: string[] = [];
  isDropdownOpen: boolean = false;
  selectedValue: string = '';
  isDisabled: boolean = false;

  private onChange = (value: string) => {};
  private onTouched = () => {};
  private closeSubscription?: Subscription;

  constructor(private searchableSelectService: SearchableSelectService) {}

  ngOnInit(): void {
    this.filteredOptions = [...this.options];
    this.isDisabled = this.disabled;
    
    // Subscribe to close all dropdowns event
    this.closeSubscription = this.searchableSelectService.closeAll$.subscribe(() => {
      if (this.isDropdownOpen) {
        this.isDropdownOpen = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.isDisabled = changes['disabled'].currentValue;
      // Close dropdown if disabled
      if (this.isDisabled) {
        this.isDropdownOpen = false;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.closeSubscription) {
      this.closeSubscription.unsubscribe();
    }
  }

  onFocus(): void {
    // Don't open dropdown if disabled
    if (this.isDisabled || this.disabled) {
      return;
    }
    // Close all other dropdowns before opening this one
    this.searchableSelectService.closeAllDropdowns();
    // Small delay to ensure other dropdowns close first
    setTimeout(() => {
      this.isDropdownOpen = true;
      // Clear search term when opening dropdown to show all options
      this.searchTerm = '';
      this.filteredOptions = [...this.options];
    }, 10);
  }

  onBlur(): void {
    // Delay to allow option click
    setTimeout(() => {
      this.isDropdownOpen = false;
      // Reset search term and show selected value when dropdown closes
      // If no option was selected, keep the search term empty and show selected value
      if (this.searchTerm && !this.options.find(opt => opt.toLowerCase() === this.searchTerm.toLowerCase())) {
        this.searchTerm = '';
      }
    }, 200);
  }

  onInput(event: Event): void {
    // Don't process input if disabled
    if (this.isDisabled || this.disabled) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchTerm = searchTerm;
    this.filterOptions(searchTerm);
    
    // Only filter options, don't set value unless it's an exact match from the list
    // The value will only be set when user selects from dropdown
    const exactMatch = this.options.find(opt => opt.toLowerCase() === searchTerm.toLowerCase());
    if (exactMatch) {
      // Only update if it matches exactly - this allows selection via typing exact match
      this.selectedValue = exactMatch;
      this.onChange(exactMatch);
      this.selectionChange.emit(exactMatch);
    }
    // Don't set value for non-matching text - it's just for searching
  }

  filterOptions(searchTerm: string): void {
    if (!searchTerm || searchTerm.trim() === '') {
      this.filteredOptions = [...this.options];
    } else {
      const searchLower = searchTerm.toLowerCase();
      this.filteredOptions = this.options.filter(opt =>
        opt.toLowerCase().includes(searchLower)
      );
    }
  }

  selectOption(option: string, event?: MouseEvent): void {
    // Don't select if disabled
    if (this.isDisabled || this.disabled) {
      return;
    }
    // Prevent input from getting focus
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.selectedValue = option;
    this.isDropdownOpen = false;
    this.searchTerm = '';
    this.filteredOptions = [...this.options];
    this.onChange(option);
    this.selectionChange.emit(option);
    // Blur the input to prevent text cursor
    const inputElement = document.getElementById(this.id) as HTMLInputElement;
    if (inputElement) {
      inputElement.blur();
    }
  }

  getInputValue(): string {
    // When dropdown is open and user is typing, show search term
    // When dropdown is closed, show selected value only
    if (this.isDropdownOpen) {
      return this.searchTerm;
    }
    return this.selectedValue || '';
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.selectedValue = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    // Close dropdown if disabled
    if (isDisabled) {
      this.isDropdownOpen = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.searchable-select-container')) {
      this.isDropdownOpen = false;
    }
  }
}

